/**
 * SectionBadge — Reusable global component for section label badges
 * Used above main headlines on every page for consistent styling
 */
import React from "react";

interface SectionBadgeProps {
  children: React.ReactNode;
  className?: string;
  /**
   * `gradient` is the treatment the home hero eyebrow carries.
   *
   * It lived as a one-off override in Home.tsx until a second page wanted the
   * same pill. Declared here so the two cannot drift; the hero keeps its own
   * class for position only.
   */
  variant?: "outline" | "gradient";
}

const GRADIENT_SURFACE = {
  background: "linear-gradient(100deg, rgba(245,166,35,.92), rgba(233,30,140,.96) 52%, rgba(139,92,246,.92))",
  borderColor: "rgba(255,255,255,.52)",
  color: "#FFFFFF",
  boxShadow: "0 0 0 1px rgba(8,12,24,.3), 0 8px 26px rgba(233,30,140,.34), 0 0 34px rgba(245,166,35,.24)",
  textShadow: "0 1px 4px rgba(8,12,24,.58)",
} as const;

export default function SectionBadge({ children, className = "", variant = "outline" }: SectionBadgeProps) {
  return (
    <div
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.5rem",
        background: "rgba(233, 30, 140, 0.1)",
        border: "1px solid rgba(233, 30, 140, 0.3)",
        borderRadius: "100px",
        padding: "0.375rem 1rem",
        marginBottom: "2rem",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#E91E8C",
        ...(variant === "gradient" ? GRADIENT_SURFACE : null),
      }}
    >
      {children}
    </div>
  );
}
